import { useState, useMemo } from "react";
import {
  photos,
  photoStats,
  photoCategories,
  recognitionTasks,
  recognitionStats,
} from "@/mocks/imageLibrary";
import type { PhotoItem, RecognitionTask } from "@/mocks/imageLibrary";

type LibraryTab = "library" | "recognition";

function PhotoGrid({ photos }: { photos: PhotoItem[] }) {
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoItem | null>(null);

  const categoryLabel: Record<string, string> = {
    scenery: "风景",
    selfie: "自拍",
    group: "合影",
    ar_sticker: "AR贴纸",
    recognition: "AI识别",
  };

  const syncConfig: Record<string, { label: string; color: string; bg: string }> = {
    synced: { label: "已同步", color: "text-emerald-700", bg: "bg-emerald-50" },
    pending: { label: "同步中", color: "text-amber-700", bg: "bg-amber-50" },
    failed: { label: "失败", color: "text-red-700", bg: "bg-red-50" },
  };

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {photos.map((photo) => {
          const sync = syncConfig[photo.syncStatus];
          return (
            <div
              key={photo.id}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all duration-200 cursor-pointer group"
              onClick={() => setSelectedPhoto(photo)}
            >
              {/* Image */}
              <div className="relative w-full h-48 overflow-hidden bg-gray-100">
                <img
                  src={photo.thumbnailUrl}
                  alt={photo.locationName}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                {/* Category Badge */}
                <span className="absolute top-2 left-2 px-2 py-0.5 bg-black/50 backdrop-blur-sm text-white text-xs rounded-full">
                  {categoryLabel[photo.category] || photo.category}
                </span>
                {/* Sync Status */}
                <span className={`absolute top-2 right-2 px-1.5 py-0.5 ${sync.bg} ${sync.color} text-xs rounded font-medium`}>
                  {sync.label}
                </span>
                {/* Recognition Indicator */}
                {photo.recognitionResult && (
                  <div className="absolute bottom-2 left-2 flex items-center gap-1 px-1.5 py-0.5 bg-teal-500/80 backdrop-blur-sm text-white text-xs rounded">
                    <i className="ri-scan-line"></i>
                    {(photo.recognitionResult.confidence * 100).toFixed(0)}%
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900 truncate">{photo.locationName}</span>
                  <span className="text-xs text-gray-400 font-mono whitespace-nowrap">{photo.deviceNo}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">{photo.takenAt.split(" ")[1]}</span>
                  <span className="text-xs text-gray-400">{photo.fileSize}</span>
                </div>
                {photo.stickerInfo && (
                  <div className="mt-1.5 flex items-center gap-1">
                    <i className="ri-emotion-line text-rose-500 text-xs"></i>
                    <span className="text-xs text-rose-600">{photo.stickerInfo.stickerName}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Photo Detail Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-8"
          onClick={() => setSelectedPhoto(null)}
        >
          <div
            className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex h-full">
              {/* Image Side */}
              <div className="flex-1 bg-gray-900 flex items-center justify-center min-h-[500px]">
                <img
                  src={selectedPhoto.fullUrl}
                  alt={selectedPhoto.locationName}
                  className="max-w-full max-h-full object-contain"
                />
              </div>

              {/* Detail Side */}
              <div className="w-80 p-5 overflow-y-auto border-l border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-gray-900">照片详情</h3>
                  <button
                    onClick={() => setSelectedPhoto(null)}
                    className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <span className="text-xs text-gray-400">拍摄地点</span>
                    <p className="text-sm font-medium text-gray-900">{selectedPhoto.locationName}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">GPS 坐标</span>
                    <p className="text-sm text-gray-700 font-mono">
                      {selectedPhoto.latitude}, {selectedPhoto.longitude}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">海拔</span>
                    <p className="text-sm text-gray-700">{selectedPhoto.altitude}m</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">拍摄时间</span>
                    <p className="text-sm text-gray-700">{selectedPhoto.takenAt}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">设备号</span>
                    <p className="text-sm text-gray-700 font-mono">{selectedPhoto.deviceNo}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">分辨率</span>
                    <p className="text-sm text-gray-700">{selectedPhoto.resolution}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">文件大小</span>
                    <p className="text-sm text-gray-700">{selectedPhoto.fileSize}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">同步状态</span>
                    <span className={`inline-flex ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedPhoto.syncStatus === "synced"
                        ? "bg-emerald-50 text-emerald-700"
                        : selectedPhoto.syncStatus === "pending"
                        ? "bg-amber-50 text-amber-700"
                        : "bg-red-50 text-red-700"
                    }`}>
                      {selectedPhoto.syncStatus === "synced" ? "已同步" : selectedPhoto.syncStatus === "pending" ? "同步中" : "失败"}
                    </span>
                  </div>

                  {/* Recognition Result */}
                  {selectedPhoto.recognitionResult && (
                    <div className="pt-3 border-t border-gray-200">
                      <span className="text-xs text-gray-400">AI 识别结果</span>
                      <div className="mt-1 p-3 bg-teal-50 rounded-lg">
                        <p className="text-sm font-medium text-teal-800">{selectedPhoto.recognitionResult.sceneName}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-teal-600">置信度 {(selectedPhoto.recognitionResult.confidence * 100).toFixed(0)}%</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {selectedPhoto.recognitionResult.tags.map((tag) => (
                            <span key={tag} className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sticker Info */}
                  {selectedPhoto.stickerInfo && (
                    <div className="pt-3 border-t border-gray-200">
                      <span className="text-xs text-gray-400">AR 贴纸</span>
                      <p className="text-sm font-medium text-rose-700">{selectedPhoto.stickerInfo.stickerName}</p>
                      <span className="text-xs text-rose-500">{selectedPhoto.stickerInfo.stickerType}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function RecognitionTab() {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const filteredTasks = useMemo(() => {
    return recognitionTasks.filter((task) => {
      const matchesSearch =
        searchQuery === "" ||
        task.sceneName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.deviceNo.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "all" || task.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [searchQuery, statusFilter]);

  const statusConfig: Record<string, { label: string; color: string; bg: string; icon: string }> = {
    completed: { label: "已完成", color: "text-emerald-700", bg: "bg-emerald-50", icon: "ri-check-line" },
    processing: { label: "识别中", color: "text-amber-700", bg: "bg-amber-50", icon: "ri-loader-4-line" },
    failed: { label: "失败", color: "text-red-700", bg: "bg-red-50", icon: "ri-close-line" },
  };

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
        {[
          { label: "识别任务总数", value: recognitionStats.totalTasks, color: "text-gray-900", bg: "bg-white", border: "border-gray-200" },
          { label: "已完成", value: recognitionStats.completed, color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
          { label: "识别中", value: recognitionStats.processing, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
          { label: "失败", value: recognitionStats.failed, color: "text-red-700", bg: "bg-red-50", border: "border-red-200" },
          { label: "平均置信度", value: `${(recognitionStats.avgConfidence * 100).toFixed(0)}%`, color: "text-teal-700", bg: "bg-teal-50", border: "border-teal-200", isPct: true },
        ].map((stat) => (
          <div key={stat.label} className={`${stat.bg} border ${stat.border} rounded-lg p-4`}>
            <div className="text-xs text-gray-500 mb-1">{stat.label}</div>
            <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索场景名 / 设备号"
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            {[
              { label: "全部", value: "all" },
              { label: "已完成", value: "completed" },
              { label: "识别中", value: "processing" },
              { label: "失败", value: "failed" },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                  statusFilter === opt.value
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Task List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <span className="text-sm text-gray-500">共 {filteredTasks.length} 条识别记录</span>
        </div>
        <div className="divide-y divide-gray-100">
          {filteredTasks.map((task) => {
            const st = statusConfig[task.status];
            return (
              <div key={task.id} className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition-colors">
                {/* Status */}
                <div className={`w-8 h-8 flex items-center justify-center rounded-full ${st.bg}`}>
                  <i className={`${st.icon} ${st.color} text-sm ${task.status === "processing" ? "animate-spin" : ""}`}></i>
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{task.sceneName}</span>
                    <span className={`px-1.5 py-0.5 ${st.bg} ${st.color} text-xs rounded font-medium`}>{st.label}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-gray-400">设备 {task.deviceNo}</span>
                    {task.status === "completed" && (
                      <span className="text-xs text-teal-600">置信度 {(task.confidence * 100).toFixed(0)}%</span>
                    )}
                    <span className="text-xs text-gray-400">
                      {task.status === "completed" ? `${task.completedAt?.split(" ")[1]}` : task.createdAt.split(" ")[1]}
                    </span>
                  </div>
                  {/* Tags */}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {task.tags.map((tag) => (
                      <span key={tag} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                {/* Confidence Bar */}
                {task.status === "completed" && (
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-teal-500 rounded-full"
                        style={{ width: `${task.confidence * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 w-8">{(task.confidence * 100).toFixed(0)}%</span>
                  </div>
                )}
                {task.status === "failed" && (
                  <button className="px-3 py-1.5 bg-red-50 text-red-600 text-xs rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap">
                    重新识别
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ImageLibraryPage() {
  const [activeTab, setActiveTab] = useState<LibraryTab>("library");
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [syncFilter, setSyncFilter] = useState("all");

  const filteredPhotos = useMemo(() => {
    return photos.filter((photo) => {
      const matchesSearch =
        searchQuery === "" ||
        photo.locationName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        photo.deviceNo.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === "all" || photo.category === categoryFilter;
      const matchesSync = syncFilter === "all" || photo.syncStatus === syncFilter;
      return matchesSearch && matchesCategory && matchesSync;
    });
  }, [searchQuery, categoryFilter, syncFilter]);

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">图片库 &amp; 拍照识别</h1>
          <p className="text-sm text-gray-500 mt-1">眼镜拍照图片库管理、AI 场景识别结果</p>
        </div>
        <button className="px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors shadow-sm whitespace-nowrap">
          <i className="ri-download-2-line mr-1.5" />
          批量导出
        </button>
      </div>

      {/* Tab Switcher */}
      <div className="flex items-center bg-gray-100 rounded-full p-1 w-fit">
        {([
          { value: "library" as const, label: "图片库", icon: "ri-image-line" },
          { value: "recognition" as const, label: "拍照识别", icon: "ri-scan-line" },
        ]).map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab.value
                ? "bg-white text-teal-700 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <i className={tab.icon}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Library Tab */}
      {activeTab === "library" && (
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {[
              { label: "图片总数", value: photoStats.total, color: "text-gray-900", bg: "bg-white", border: "border-gray-200" },
              { label: "今日新增", value: photoStats.todayNew, color: "text-teal-700", bg: "bg-teal-50", border: "border-teal-200" },
              { label: "已同步", value: photoStats.synced, color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
              { label: "同步中", value: photoStats.pending, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
              { label: "失败", value: photoStats.failed, color: "text-red-700", bg: "bg-red-50", border: "border-red-200" },
              { label: "已识别", value: photoStats.withRecognition, color: "text-purple-700", bg: "bg-purple-50", border: "border-purple-200" },
            ].map((stat) => (
              <div key={stat.label} className={`${stat.bg} border ${stat.border} rounded-lg p-4`}>
                <div className="text-xs text-gray-500 mb-1">{stat.label}</div>
                <div className={`text-2xl font-bold ${stat.color}`}>{stat.value.toLocaleString()}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-48">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索地点 / 设备号"
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                />
              </div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              >
                {photoCategories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label} ({cat.count})
                  </option>
                ))}
              </select>
              <select
                value={syncFilter}
                onChange={(e) => setSyncFilter(e.target.value)}
                className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              >
                <option value="all">全部状态</option>
                <option value="synced">已同步</option>
                <option value="pending">同步中</option>
                <option value="failed">失败</option>
              </select>
              {(searchQuery || categoryFilter !== "all" || syncFilter !== "all") && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setCategoryFilter("all");
                    setSyncFilter("all");
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700 whitespace-nowrap"
                >
                  重置筛选
                </button>
              )}
            </div>
          </div>

          {/* Photo Grid */}
          <PhotoGrid photos={filteredPhotos} />
        </div>
      )}

      {/* Recognition Tab */}
      {activeTab === "recognition" && <RecognitionTab />}
    </div>
  );
}

export default ImageLibraryPage;